import { z } from "zod"

/**
 * Маркетплейс запросов покупателей (Э6, fd-request-*).
 *
 * Самостоятельная сущность запроса, не привязанная к товару: покупатель
 * публикует потребность («ищу такой-то товар» / «нужно изготовить»), а продавцы
 * соответствующей категории откликаются предложениями. Отклик — это тот же
 * custom_offer из чат-контура, поэтому DTO отклика (`RequestOffer`) отдаётся
 * рядом с деталями запроса, а принять его покупатель может уже существующей
 * ручкой `POST /buyer/offers/:id/accept`.
 */

export const BuyerRequestTypeSchema = z.enum(["search", "manufacture"])
export type BuyerRequestType = z.infer<typeof BuyerRequestTypeSchema>

export const BuyerRequestStatusSchema = z.enum(["open", "in_progress", "closed", "cancelled"])
export type BuyerRequestStatus = z.infer<typeof BuyerRequestStatusSchema>

export const BuyerRequestAttachmentKindSchema = z.enum(["image", "file"])
export type BuyerRequestAttachmentKind = z.infer<typeof BuyerRequestAttachmentKindSchema>

export const BuyerRequestAttachmentSchema = z.object({
  id: z.string(),
  url: z.string(),
  kind: BuyerRequestAttachmentKindSchema,
  createdAt: z.string(),
})
export type BuyerRequestAttachment = z.infer<typeof BuyerRequestAttachmentSchema>

/** Статус отклика продавца на запрос (жизненный цикл custom_offers). */
export const RequestOfferStatusSchema = z.enum([
  "active",
  "accepted",
  "cancelled",
  "expired",
  "declined",
])
export type RequestOfferStatus = z.infer<typeof RequestOfferStatusSchema>

/** Отклик продавца на запрос — деталь для карточки покупателя (fd-request-11a). */
export const RequestOfferSchema = z.object({
  id: z.string(),
  requestId: z.string(),
  conversationId: z.string(),
  shopId: z.string(),
  shopName: z.string(),
  buyerUserId: z.string(),
  /** Товар-аналог, если продавец откликнулся готовым товаром (иначе кастом). */
  productId: z.string().optional(),
  productName: z.string().optional(),
  productPhoto: z.string().optional(),
  description: z.string(),
  quantity: z.number().int().positive(),
  unitPrice: z.number().int().nonnegative(),
  productionEstimate: z.string(),
  selectedOptions: z.array(z.string()),
  /** Фото к отклику (fd-request-10). Старый шлюз поля не шлёт. */
  photos: z.array(z.string()).optional(),
  status: RequestOfferStatusSchema,
  expiresAt: z.string().optional(),
  acceptedAt: z.string().optional(),
  /** true, когда покупатель уже принял отклик и он лёг в корзину/заказ. */
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type RequestOffer = z.infer<typeof RequestOfferSchema>

export const BuyerRequestSchema = z.object({
  id: z.string(),
  humanCode: z.string(),
  buyerUserId: z.string(),
  type: BuyerRequestTypeSchema,
  categoryId: z.string(),
  categoryName: z.string().optional(),
  title: z.string(),
  description: z.string(),
  deliveryRegion: z.string(),
  budgetMin: z.number().int().nonnegative().optional(),
  budgetMax: z.number().int().nonnegative().optional(),
  /** Срок (дата, ISO `YYYY-MM-DD`). */
  deadline: z.string().optional(),
  status: BuyerRequestStatusSchema,
  attachments: z.array(BuyerRequestAttachmentSchema),
  /** Число откликов — считается по custom_offers запроса. */
  offerCount: z.number().int().nonnegative(),
  /** Можно ли ещё редактировать: true, пока нет ни одного отклика (fd-request-17). */
  editable: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type BuyerRequest = z.infer<typeof BuyerRequestSchema>

/** Детали запроса с откликами — для экрана покупателя (fd-request-11a). */
export const BuyerRequestDetailSchema = BuyerRequestSchema.extend({
  offers: z.array(RequestOfferSchema),
})
export type BuyerRequestDetail = z.infer<typeof BuyerRequestDetailSchema>

export const BuyerRequestListSchema = z.object({
  items: z.array(BuyerRequestSchema),
  total: z.number().int().nonnegative(),
})
export type BuyerRequestList = z.infer<typeof BuyerRequestListSchema>

// --- Запросы на запись ------------------------------------------------------

export const CreateBuyerRequestSchema = z.object({
  type: BuyerRequestTypeSchema,
  categoryId: z.string().min(1),
  title: z.string().trim().max(200).optional(),
  description: z.string().trim().min(2).max(4000),
  deliveryRegion: z.string().trim().max(200).optional(),
  budgetMin: z.number().int().nonnegative().optional(),
  budgetMax: z.number().int().nonnegative().optional(),
  deadline: z.string().trim().max(40).optional(),
})
export type CreateBuyerRequest = z.infer<typeof CreateBuyerRequestSchema>

/** Правка запроса разрешена только до первого отклика (fd-request-17). */
export const UpdateBuyerRequestSchema = z
  .object({
    type: BuyerRequestTypeSchema.optional(),
    categoryId: z.string().min(1).optional(),
    title: z.string().trim().max(200).optional(),
    description: z.string().trim().min(2).max(4000).optional(),
    deliveryRegion: z.string().trim().max(200).optional(),
    budgetMin: z.number().int().nonnegative().nullable().optional(),
    budgetMax: z.number().int().nonnegative().nullable().optional(),
    deadline: z.string().trim().max(40).nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: "Nothing to update" })
export type UpdateBuyerRequest = z.infer<typeof UpdateBuyerRequestSchema>

/** Лента запросов для продавца (fd-request-03/20). */
export const SellerRequestSchema = BuyerRequestSchema.extend({
  /** Уже ли этот магазин откликнулся на запрос. */
  respondedByShop: z.boolean(),
})
export type SellerRequest = z.infer<typeof SellerRequestSchema>

export const SellerRequestListSchema = z.object({
  items: z.array(SellerRequestSchema),
  total: z.number().int().nonnegative(),
})
export type SellerRequestList = z.infer<typeof SellerRequestListSchema>

/**
 * Отклик продавца на запрос: товаром-аналогом или условиями без товара
 * (fd-request-09/10). С товаром покупатель принимает отклик в корзину, без
 * товара - в чат, где продавец выставляет смету.
 */
export const RespondToRequestSchema = z.object({
  /** Товар-аналог магазина. Необязателен с 31.10.2026. */
  productId: z.string().min(1).optional(),
  description: z.string().trim().min(2).max(1000),
  quantity: z.number().int().positive(),
  unitPrice: z.number().int().positive(),
  productionEstimate: z.string().trim().max(200).optional(),
  selectedOptions: z.array(z.string()).optional(),
  /** Фото к отклику: ссылки на загруженные изображения (до 10). */
  photos: z.array(z.string().url().max(2048)).max(10).optional(),
  weightGrams: z.number().int().positive().optional(),
  lengthCm: z.number().int().positive().optional(),
  widthCm: z.number().int().positive().optional(),
  heightCm: z.number().int().positive().optional(),
  expiresAt: z.string().trim().max(80).optional(),
})
export type RespondToRequest = z.infer<typeof RespondToRequestSchema>
